import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

// Singleton axios client with auth interceptors
let apiClient: AxiosInstance | null = null;

const getApiClient = (): AxiosInstance => {
  if (apiClient) return apiClient;

  apiClient = axios.create({});

  // Attach Authorization header on every request with latest token
  apiClient.interceptors.request.use(async (config) => {
    const { tokens } = await fetchAuthSession();
    // Prefer ID token for API Gateway Cognito User Pool authorizers
    const token = tokens?.idToken?.toString() || tokens?.accessToken?.toString();
    if (token) {
      const headers = (config.headers ?? {}) as any;
      headers.Authorization = `Bearer ${token}`;
      config.headers = headers;
    }

    // Only set JSON content-type when sending a body
    const hasBody = typeof config.data !== 'undefined';
    if (hasBody) {
      const headers = (config.headers ?? {}) as any;
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      config.headers = headers;
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (res) => res,
    (error) => Promise.reject(error)
  );

  return apiClient;
};

// Authenticated helpers reuse the singleton client
export const authenticatedGet = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => getApiClient().get<T>(url, config);

export const authenticatedPost = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => getApiClient().post<T>(url, data, config);

export const authenticatedPut = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => getApiClient().put<T>(url, data, config);

export const authenticatedDelete = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => getApiClient().delete<T>(url, config);

// Stripe API Endpoints
const STRIPE_CREATE_CHECKOUT_SESSION_ENDPOINT = process.env.REACT_APP_LAMBDA_STRIPE_CREATE_CHECKOUT_SESSION;

// Stripe-specific types
export interface CreateCheckoutSessionRequest {
  quantity: number;
  bookId?: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
  pricing: {
    quantity: number;
    price_per_image: number;
    total_price: number;
    name: string;
  };
}

// Book pricing types
export interface PricingTier {
  minQuantity: number;
  maxQuantity: number;
  pricePerImage: number;
  description: string;
}

export interface BookPricingInfo {
  bookId?: string;
  chunkCount: number;
  recommendedQuantity: number;
  recommendedPricing: {
    quantity: number;
    pricePerImage: number;
    totalPrice: number;
    tier: string;
  };
  pricingTiers: PricingTier[];
}

export interface AddCreditsRequest {
  userId: string;
  amount: number;
  stripeSessionId?: string;
  isAdminRequest?: boolean;
}

export interface AddCreditsResponse {
  userId: string;
  newAvailableImages: number;
  creditsAdded: number;
}

// Stripe API functions
export const createStripeCheckoutSession = async (
  request: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> => {
  if (!STRIPE_CREATE_CHECKOUT_SESSION_ENDPOINT) {
    throw new Error('Stripe checkout endpoint not configured');
  }

  const response = await authenticatedPost<CreateCheckoutSessionResponse>(
    STRIPE_CREATE_CHECKOUT_SESSION_ENDPOINT,
    request
  );

  return response.data;
};

// Download Book types and API
export interface GetPresignedBookUrlResponse {
  presignedUrl: string;
  expiresIn: number;
  isIllustrated?: boolean;
  bookMetadata?: {
    title?: string;
    author?: string;
    fileName?: string;
  };
  versionInfo?: {
    path: string;
    timestamp: string;
    selectedImages?: string | 'all';
  };
  hasIllustratedVersion?: boolean;
  illustratedVersionTimestamp?: string;
}

export const getPresignedBookUrl = async (
  fileId: string,
  versionTimestamp?: string,
  forceDownload: boolean = false
): Promise<GetPresignedBookUrlResponse> => {
  const endpoint = process.env.REACT_APP_LAMBDA_GET_PRESIGNED_URL_BOOK;
  if (!endpoint) throw new Error('Download endpoint not configured');

  const url = new URL(endpoint);
  url.searchParams.set('FileId', fileId);
  if (versionTimestamp) url.searchParams.set('VersionTimestamp', versionTimestamp);
  if (forceDownload) url.searchParams.set('ForceDownload', '1');

  const res = await authenticatedGet<GetPresignedBookUrlResponse>(url.toString());
  return res.data;
};

// Enhanced credit addition function that supports Stripe verification
export const addUserCredits = async (
  request: AddCreditsRequest
): Promise<AddCreditsResponse> => {
  const addCreditsEndpoint = process.env.REACT_APP_LAMBDA_ADD_USER_IMAGE_CREDITS;
  
  if (!addCreditsEndpoint) {
    throw new Error('Add credits endpoint not configured');
  }

  const response = await authenticatedPost<AddCreditsResponse>(
    addCreditsEndpoint,
    request
  );

  return response.data;
};

// Utility function to handle the complete purchase flow
export const purchaseImageCredits = async (
  quantity: number,
  bookId?: string
): Promise<string> => {
  try {
    const checkoutResponse = await createStripeCheckoutSession({
      quantity,
      bookId
    });
    
    // Return the checkout URL to redirect the user
    return checkoutResponse.checkout_url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error('Failed to initiate purchase');
  }
};

// Calculate price for any quantity using tiered pricing
export const calculatePriceForQuantity = (
  quantity: number, 
  pricingTiers: PricingTier[]
): { pricePerImage: number; totalPrice: number; tier: string } | null => {
  if (quantity < 10) return null;
  
  // Tiered pricing structure (matches backend)
  // First 10 images: $0.20 each
  // Next 39 images (11-49): $0.18 each  
  // Next 50 images (50-99): $0.16 each
  // Remaining images (100+): $0.15 each
  
  let totalPrice = 0.0;
  let remainingQuantity = quantity;
  
  // First tier: 0-10 images at $0.20
  if (remainingQuantity > 0) {
    const tier1Quantity = Math.min(remainingQuantity, 10);
    totalPrice += tier1Quantity * 0.20;
    remainingQuantity -= tier1Quantity;
  }
  
  // Second tier: 11-49 images at $0.18
  if (remainingQuantity > 0) {
    const tier2Quantity = Math.min(remainingQuantity, 39); // 49 - 10 = 39
    totalPrice += tier2Quantity * 0.18;
    remainingQuantity -= tier2Quantity;
  }
  
  // Third tier: 50-99 images at $0.16
  if (remainingQuantity > 0) {
    const tier3Quantity = Math.min(remainingQuantity, 50); // 99 - 49 = 50
    totalPrice += tier3Quantity * 0.16;
    remainingQuantity -= tier3Quantity;
  }
  
  // Fourth tier: 100+ images at $0.15
  if (remainingQuantity > 0) {
    totalPrice += remainingQuantity * 0.15;
  }
  
  // Calculate average price per image
  const avgPricePerImage = totalPrice / quantity;
  
  return {
    pricePerImage: Number(avgPricePerImage.toFixed(3)),
    totalPrice: Number(totalPrice.toFixed(2)),
    tier: `Tiered pricing (${quantity} images)`
  };
};

// Test Image API functions
export interface TestImage {
  subject: string;
  prompt: string;
  timestamp: number;
  imageUrl: string;
  fileId: string;
  compositeKey: string;
  isTestImage: boolean;
}

export interface GetTestImagesResponse {
  images: TestImage[];
}

export const getTestImages = async (fileId: string): Promise<TestImage[]> => {
  const endpoint = process.env.REACT_APP_LAMBDA_GET_USER_IMAGES;
  if (!endpoint) throw new Error('Get user images endpoint not configured');

  const url = new URL(endpoint);
  url.searchParams.set('FileId', fileId);
  url.searchParams.set('testimage', 'true');

  const response = await authenticatedGet<GetTestImagesResponse>(url.toString());
  return response.data.images;
};

// Account Deletion API
export interface DeleteAccountResponse {
  message: string;
  deletionResults: {
    books_deleted: number;
    credits_deleted: boolean;
    cognito_deleted: boolean;
    errors: string[];
  };
}

export const deleteUserAccount = async (): Promise<DeleteAccountResponse> => {
  const endpoint = process.env.REACT_APP_LAMBDA_DELETE_ACCOUNT;
  if (!endpoint) throw new Error('Delete account endpoint not configured');

  const response = await authenticatedPost<DeleteAccountResponse>(endpoint, {});
  return response.data;
}; 